// src/pages/SolutionPage.js
import React, { useEffect } from "react"; // Import useEffect
import { useLocation } from "react-router-dom"; // Import useLocation

import WhiteContactForm from "../components/WhiteContactForm";
import LogoScroller from "../components/LogoScroller";

// --- Import your icons ---
import iconBpmn1 from "../assets/images/icon-designer.svg";
import iconBpmn2 from "../assets/images/icon-automation.svg";
import iconBpmn3 from "../assets/images/icon-monitoring.svg";
import iconBpmn4 from "../assets/images/icon-compliance.svg";
import iconBpsc1 from "../assets/images/icon-designer.svg";
import iconBpsc2 from "../assets/images/icon-automation.svg";
import iconBpsc3 from "../assets/images/icon-monitoring.svg";
import iconFrontier1 from "../assets/images/icon-automation.svg";
import iconFrontier2 from "../assets/images/icon-monitoring.svg";
import iconFrontier3 from "../assets/images/icon-designer.svg";
import iconAdapters1 from "../assets/images/icon-designer.svg";
import iconAdapters2 from "../assets/images/icon-automation.svg";
import iconAdapters3 from "../assets/images/icon-monitoring.svg";
import iconAdapters4 from "../assets/images/secure.svg";
import iconAdapters5 from "../assets/images/icon-compliance.svg";

// --- Import Framer Motion and AnimatedSection ---
import { motion } from "framer-motion";
import AnimatedSection, { fadeIn, fadeInUp, fadeInLeft, scaleUp, staggerItemFadeInUp } from "../components/AnimatedSection"; // Adjust path if needed

// --- Data for each section ---
const sectionsData = [
  {
    id: "bpmn",
    title: "Revolutionize Blockchain Workflows with BloxBlueprint BPModeler",
    description:
      "Intuitively model, visualize, and execute blockchain business processes using BPMN 2.0—enhanced with Web3 capabilities for greater agility, transparency, and compliance.",
    videoSrc:
      "https://zenithblox.blob.core.windows.net/videos/BPMN_solution.mp4?sp=r&st=2025-06-11T15:30:07Z&se=2050-06-11T23:30:07Z&spr=https&sv=2024-11-04&sr=b&sig=MgTEsdYFfbApzB10eeBts%2BGXpulMpoLT88YyubRO0Ec%3D",
    features: [
      { icon: iconBpmn1, text: "Flow Objects: Core building blocks like events, activities, and gateways that define process behavior." },
      { icon: iconBpmn2, text: "Connecting Objects: Link flow elements and show process direction using sequence, message, and associations." },
      { icon: iconBpmn3, text: "Swimlanes: Group activities by responsibility using pools and lanes to show who handles each part of the process." },
      { icon: iconBpmn4, text: "Artifacts: Add extra details like data objects, groups, and annotations to enrich and explain the process clearly." },
    ],
  },
  {
    id: "bpsc",
    title: "Simplify Compliance, Cut Risk with BPSC Compiler",
    description:
      "Compliance is essential in the regulated financial industry. ZenithBlox's BPSC Compiler simplifies regulatory management, making blockchain adoption secure.",
    videoSrc:
      "https://zenithblox.blob.core.windows.net/videos/BPSC_solution.mp4?sp=r&st=2025-06-11T15:33:06Z&se=2050-06-11T23:33:06Z&spr=https&sv=2024-11-04&sr=b&sig=EleJEnG4xnNgsgZk0vFh%2Fb1tCST8nkkIMMD2M4%2FhqvU%3D",
    features: [
      { icon: iconBpsc1, text: "No More Manual Coding:Eliminate hand-coding smart contracts' time-consuming and error-prone process." },
      { icon: iconBpsc2, text: "Continuous Compliance: Ensure your blockchain applications adhere to the latest regulations, even as they evolve.." },
      { icon: iconBpsc3, text: "Reduced Risk: Minimize the risk of legal and financial penalties due to non-compliance." },
    ],
  },
  {
    id: "frontierbox",
    title: "Ensure Secure Transactions with FrontierBox Engine",
    description:
      "In finance, trust and security are crucial. The FrontierBlox Engine offers unmatched protection for your blockchain transactions, allowing you to innovate with confidence.",
    videoSrc:
      "https://zenithblox.blob.core.windows.net/videos/FrontierBlox_solution.mp4?sp=r&st=2025-06-11T15:35:21Z&se=2060-06-11T23:35:21Z&spr=https&sv=2024-11-04&sr=b&sig=XEJfWseMlmo3HRztiBHrgCjqABMXlkCZg2VVl%2Bg7ApA%3D",
    features: [
      {
        icon: iconFrontier1,
        text: "Real-time Monitoring: Every transaction is analyzed in real-time to ensure it adheres to your pre-defined business rules and security protocols.",
      },
      {
        icon: iconFrontier2,
        text: "Dynamic Policy Enforcement: Customize policies to fit your specific needs, and the engine automatically enforces them, preventing unauthorized actions or fraud.",
      },
      {
        icon: iconFrontier3,
        text: "Fraud Detection & Prevention: Advanced algorithms and machine learning help identify suspicious patterns and potential threats, keeping your assets safe.",
      },
    ],
  },
  {
    id: "adapters",
    title: "Connect, Transform, Innovate with Universal Adapters",
    description:
      "Rooted in a clear purpose, our mission drives everything we do today—while our vision lights the way toward the future we’re working to create.",
    videoSrc:
      "https://zenithblox.blob.core.windows.net/videos/UA_solution.mp4?sp=r&st=2025-06-11T15:37:36Z&se=2060-06-11T23:37:36Z&spr=https&sv=2024-11-04&sr=b&sig=fENT4sO%2FS7QDPXmDnGs3034Fge2E5XBfxfNgtVATk7c%3D",
    features: [
      { icon: iconAdapters1, text: "Connect to Blockchain: Link systems to public or private chains with ease" },
      { icon: iconAdapters2, text: "Unlock Data: Map data between different formats for seamless communication." },
      { icon: iconAdapters3, text: "Utilize Smart Contracts: Deploy and manage smart contracts across platforms." },
      { icon: iconAdapters4, text: "Ensure Security: Protect sensitive financial information with secure protocols." },
      { icon: iconAdapters5, text: "Unified API Gateway: Access blockchain services via one simple interface." },
    ],
  },
];

// == Section Component (Renders individual solution sections) ==
// --- MODIFICATION: Accept 'id' prop ---
const Section = ({ id, title, description, videoSrc, features }) => {
  const numFeatures = features.length;
  let gridClass = "grid grid-cols-1 sm:grid-cols-2 gap-8";

  if (numFeatures === 3) {
    gridClass += " md:grid-cols-3";
  } else if (numFeatures === 5) {
    gridClass += " md:grid-cols-3 lg:grid-cols-5";
  } else {
    gridClass += " md:grid-cols-4";
  }

  return (
    // --- MODIFICATION: Add 'id' to the section element ---
    <AnimatedSection
      id={id}
      tag="section"
      triggerOnce={false}
      animationVariants={fadeIn}
      className="py-16 px-0 md:px-5 mb-10 border-b border-gray-800 last:border-b-0"
    >
      <AnimatedSection triggerOnce={false} animationVariants={fadeInLeft} className="mb-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl lg:text-3xl font-medium mb-3 text-white relative pl-4">
          <span className="absolute left-0 top-1 bottom-1 w-1 bg-purple-600 rounded"></span>
          {title}
        </h2>
        <p className="text-base text-gray-400 max-w-3xl">{description}</p>
      </AnimatedSection>

      <AnimatedSection
        triggerOnce={false}
        animationVariants={scaleUp}
        className="bg-gray-950 border border-gray-800 rounded-lg mb-10 overflow-hidden flex justify-center items-center min-h-[300px] sm:min-h-[400px] lg:min-h-[500px] max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <video className="w-full h-full  block" controls playsInline loop>
          <source src={videoSrc} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </AnimatedSection>
      {id === "adapters" && (
        <div className="my-16">
          <LogoScroller />
        </div>
      )}
      <AnimatedSection
        triggerOnce={false}
        animationVariants={fadeIn}
        staggerChildren={0.1}
        className={`${gridClass} max-w-6xl mx-auto px-4 sm:px-6 lg:px-8`}
      >
        {features.map((feature, index) => (
          <motion.div
            key={index}
            variants={staggerItemFadeInUp}
            whileHover={{
              scale: 1.03,
              boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.15)",
              borderColor: "#A855F7", // Purple-500
              borderWidth: "2px", // Make border thicker on hover
            }}
            transition={{ type: "spring", stiffness: 350, damping: 15 }}
            className="bg-white border border-gray-200 rounded-lg p-8 text-left flex flex-col min-h-[200px] shadow-lg"
          >
            <img src={feature.icon} alt="" className="h-10 w-10 mb-5" />
            <p className="text-base text-gray-800 leading-relaxed flex-grow">{feature.text}</p>
          </motion.div>
        ))}
      </AnimatedSection>
    </AnimatedSection>
  );
};

// == Main SolutionPage Component ==
const SolutionPage = () => {
  // --- ADDITION: Logic to handle scrolling to hash links ---
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        // We use a small timeout to ensure the element is rendered before scrolling
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [hash]); // Rerun effect when hash changes
  // --- END ADDITION ---

  return (
    <div className="min-h-screen bg-black font-sans">
      {/* Section 1: Black Background Hero */}
      <div className="bg-black text-white">
        <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-left py-24 md:py-32">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6 leading-tight mt-12">
            Solutions Built for the <br /> Future of Finance
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl leading-relaxed">
            ZenithBlox focuses on finance, offering customizable, proactive solutions with real-time transparency for secure blockchain operations.
          </p>
        </AnimatedSection>
      </div>

      {/* Section 2: White Background Intro */}
      <div className="bg-white text-black">
        <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-left py-24 md:py-32">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6 leading-tight text-gray-900">
            Seamlessly Connect Your <br /> Systems to Blockchain
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl leading-relaxed">
            Rooted in a clear purpose, our mission drives everything we do today— while our vision lights the way toward the future we're working to
            create.
          </p>
        </AnimatedSection>
      </div>

      <div className="bg-black text-white">
        <div className="pt-16">
          {sectionsData.map((section) => (
            // --- MODIFICATION: Pass 'id' prop ---
            <Section
              key={section.id}
              id={section.id}
              title={section.title}
              description={section.description}
              videoSrc={section.videoSrc}
              features={section.features}
            />
          ))}
        </div>
      </div>
      <AnimatedSection triggerOnce={false} animationVariants={fadeInUp} threshold={0.05}>
        <WhiteContactForm />
      </AnimatedSection>
    </div>
  );
};

export default SolutionPage;
